import React, { useMemo } from 'react'
import Link from '../Link/index'
import { H4 } from '../Heading/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import GaiaLogo, { TYPE_WHITE_PLUS } from '../GaiaLogo/index'
import {
  DISPLAY_MODE_TILE_LIST_GAIA_PLUS,
  TYPE_CONTINUE_WATCHING,
  TYPE_PLAYLIST,
} from '../../services/pm-section/index'
import SliderBar from '../SliderBar/index'

/**
 * @typedef {Object} SectionTilesListHeaderProps
 * @property {string} sectionType - Type of the section.
 * @property {string} displayMode - Display mode of the section.
 * @property {string} sectionTitle - Title of the section.
 * @property {string} footerLabel - Footer label for the section.
 * @property {string} defaultTabLabel - Default tab label.
 * @property {Function} onTabChange - Callback function to handle tab change.
 * @property {Array} tabs - Array of tab items.
 * @property {Object} viewAll - Flag to indicate if "View All" is enabled.
 * @property {Function} onViewAllClick - Callback function to handle viewAll click.

/**
 * SectionTilesListHeader component renders the header of tiles list section.
 *
 * @param {SectionTilesListHeaderProps} props - Props for the SectionTilesListHeader component.
 * @returns {JSX.Element} The rendered SectionTilesListHeader component.
 */

function SectionTilesListHeader (props) {
  const {
    sectionType,
    displayMode,
    sectionTitle,
    footerLabel,
    defaultTabLabel,
    onTabChange,
    tabs,
    viewAll,
    onViewAllClick,
  } = props

  const tabItems = useMemo(() => {
    return tabs.map((tab) => {
      const tabName = tab.get('tabLabel')
      return {
        label: tabName,
        value: tabName,
      }
    })
  }, [tabs])

  const showTabs = (tabs && tabs.size > 1)
    || [TYPE_CONTINUE_WATCHING, TYPE_PLAYLIST].includes(sectionType)

  if (displayMode === DISPLAY_MODE_TILE_LIST_GAIA_PLUS) {
    return (
      <div className="section-tiles-list__header section-tiles-list__header--gaia-plus">
        <div className="section-tiles-list__header-plus">
          <div className="section-tiles-list__header-plus-logo">
            <GaiaLogo inline type={TYPE_WHITE_PLUS} />
            <p className="section-tiles-list__header-plus-title">{sectionTitle}</p>
          </div>
        </div>
        <p className="section-tiles-list__header-plus-text">{footerLabel}</p>
      </div>
    )
  }

  return (
    <div className="section-tiles-list__header">
      {viewAll ? (
        <div className="section-tiles-list__title-wrapper">
          <Link
            to={viewAll.get('url')}
            onClick={onViewAllClick}
            className="section-tiles-list__link"
          >
            <H4 className="section-tiles-list__title">
              {sectionTitle}
              <IconV2
                className="section-tiles-list__icon"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </H4>
          </Link>
          <Link
            to={viewAll.get('url')}
            onClick={onViewAllClick}
            className="section-tiles-list__link"
          >
            <div className="section-tiles-list__view-all">
              <span className="section-tiles-list__label">{viewAll.get('label')}</span>
              <IconV2
                className="section-tiles-list__icon"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </Link>
        </div>
      ) : (
        <H4 className="section-tiles-list__title">{sectionTitle}</H4>
      )}
      {showTabs
        ? <SliderBar
          className="section-tiles-list__tabs"
          items={tabItems}
          onclick={onTabChange}
          initValue={defaultTabLabel}
        />
        : null
      }
    </div>
  )
}

export default SectionTilesListHeader
